
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Bell, Mail, Smartphone, Globe, Radio } from 'lucide-react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

const RadioNotificationSettings: React.FC = () => {
  const { toast } = useToast();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [notificationFrequency, setNotificationFrequency] = useState("instant");

  const saveSettings = () => {
    toast({
      title: "Notification settings saved",
      description: "Your radio tracking notification preferences have been updated"
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Radio Spin Notifications</CardTitle>
          <CardDescription>Configure how you want to be notified about radio plays</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="email-notifications" className="text-sm font-medium">
                  Email Notifications
                </Label>
              </div>
              <Switch
                id="email-notifications"
                checked={emailNotifications}
                onCheckedChange={setEmailNotifications}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="push-notifications" className="text-sm font-medium">
                  Push Notifications
                </Label>
              </div>
              <Switch
                id="push-notifications"
                checked={pushNotifications}
                onCheckedChange={setPushNotifications}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="in-app-notifications" className="text-sm font-medium">
                  In-App Notifications
                </Label>
              </div>
              <Switch
                id="in-app-notifications"
                checked={inAppNotifications}
                onCheckedChange={setInAppNotifications}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notification-frequency">Notification Frequency</Label>
            <Select
              value={notificationFrequency}
              onValueChange={setNotificationFrequency}
            >
              <SelectTrigger id="notification-frequency">
                <SelectValue placeholder="Select Frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instant">Real-time (Instant)</SelectItem>
                <SelectItem value="hourly">Hourly Digest</SelectItem>
                <SelectItem value="daily">Daily Summary</SelectItem>
                <SelectItem value="weekly">Weekly Report</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={saveSettings} className="w-full">Save Notification Settings</Button>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Recent Notifications</CardTitle>
          <CardDescription>Latest alerts about your radio plays</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Track</TableHead>
                <TableHead>Station</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Midnight Dream</TableCell>
                <TableCell>KEXP 90.3 FM</TableCell>
                <TableCell>Seattle, USA</TableCell>
                <TableCell>5 minutes ago</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Summer Haze</TableCell>
                <TableCell>BBC Radio 1</TableCell>
                <TableCell>London, UK</TableCell>
                <TableCell>2 hours ago</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Echoes</TableCell>
                <TableCell>Triple J</TableCell>
                <TableCell>Sydney, Australia</TableCell>
                <TableCell>Yesterday, 15:42</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Midnight Dream</TableCell>
                <TableCell>Radio Nova</TableCell>
                <TableCell>Paris, France</TableCell>
                <TableCell>Yesterday, 10:15</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Lost in Translation</TableCell>
                <TableCell>SiriusXM Alt Nation</TableCell>
                <TableCell>USA</TableCell>
                <TableCell>2 days ago</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Regional Preferences</CardTitle>
          <CardDescription>Configure notifications by geographic region</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="global-notifications" className="text-sm font-medium">
                  Global Notifications
                </Label>
              </div>
              <Switch id="global-notifications" defaultChecked />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Radio className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="major-stations-only" className="text-sm font-medium">
                  Major Stations Only
                </Label>
              </div>
              <Switch id="major-stations-only" defaultChecked={false} />
            </div>
            
            <div className="space-y-2">
              <Label>Priority Regions</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch id="region-na" defaultChecked />
                  <Label htmlFor="region-na">North America</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="region-eu" defaultChecked />
                  <Label htmlFor="region-eu">Europe</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="region-asia" defaultChecked />
                  <Label htmlFor="region-asia">Asia</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="region-aus" defaultChecked />
                  <Label htmlFor="region-aus">Australia</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="region-sa" defaultChecked={false} />
                  <Label htmlFor="region-sa">South America</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="region-af" defaultChecked={false} />
                  <Label htmlFor="region-af">Africa</Label>
                </div>
              </div>
            </div>
          </div>
          
          <Button onClick={saveSettings} className="w-full">Save Regional Preferences</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default RadioNotificationSettings;
